import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { LanguageProvider } from './contexts/LanguageContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { AccessibilityProvider } from './contexts/AccessibilityContext';
import { ExternalLinkProvider } from './components/ExternalLinkWarning';
import AccessibilityBar from './components/AccessibilityBar';
import LogoSection from './components/LogoSection';
import Navbar from './components/Navbar';
import NewsTicker from './components/NewsTicker';
import WatermarkBackground from './components/WatermarkBackground';
import GoToTopButton from './components/GoToTopButton';
import Footer from './components/Footer';
import RouteTracker from './components/RouteTracker';
import ScrollToTop from './components/ScrollToTop';
import { initializePageIndex } from './lib/searchRegistry';
import { PAGE_SEARCH_INDEX } from './lib/pageIndex';
import Home from './pages/Home';
import Overview from './pages/Overview';
import AdministrativeStructure from './pages/AdministrativeStructure';
import BoardProject from './pages/BoardProject';
import AnnualReport from './pages/AnnualReport';
import Circulars from './pages/Circulars';
import WorkersWelfare from './pages/WorkersWelfare';
import RightToInfo from './pages/RightToInfo';
import RightToService from './pages/RightToService';
import TendersNotices from './pages/TendersNotices';
import PhotoGallery from './pages/MediaGallery/PhotoGallery';
import VideoGallery from './pages/MediaGallery/VideoGallery';
import Services from './pages/Services';
import ListOfServices from './pages/ListOfServices';
import ImportantCirculars from './pages/ImportantCirculars';
import Dashboard from './pages/Dashboard';
import Contact from './pages/Contact';
import ContactSOP from './pages/ContactSOP';
import SiteMap from './pages/SiteMap';
import Grievance from './pages/Grievance';
import PrivacyPolicy from './pages/PrivacyPolicy';
import ScreenReader from './pages/ScreenReader';
import FAQ from './pages/FAQ';
import Schemes from './pages/Schemes';
import Activities from './pages/Activities';
import Programs from './pages/Programs';
import Awards from './pages/Awards';
import Feedback from './pages/Feedback';
import HundredDaysProgram from './pages/HundredDaysProgram';
import BoardMembers from './pages/BoardMembers';
import './App.css';

function App() {
  const [isStickyNavbar, setIsStickyNavbar] = useState(false);

  // Initialize all pages for search on app load
  // This ensures first-time visitors can search all pages immediately
  useEffect(() => {
    initializePageIndex(PAGE_SEARCH_INDEX);
  }, []);

  // Handle scroll effect for main content
  useEffect(() => {
    let ticking = false;
    
    const getScrollThreshold = () => {
      const width = window.innerWidth;
      if (width <= 480) return 80; // Small mobile
      if (width <= 768) return 90; // Mobile
      if (width <= 1024) return 95; // Tablet
      return 100; // Desktop
    };
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollY = window.scrollY;
          const threshold = getScrollThreshold();
          // Adjust main content when navbar is sticky
          setIsStickyNavbar(scrollY > threshold);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  return (
    <LanguageProvider>
      <ThemeProvider>
        <AccessibilityProvider>
          <ExternalLinkProvider>
            <Router>
            <ScrollToTop />
            <RouteTracker />
            <div className="App">
              <WatermarkBackground />
              <AccessibilityBar />
              <div className="header-section">
                <LogoSection />
                <Navbar />
              </div>
              <NewsTicker />
              <main className={`main-content ${isStickyNavbar ? 'sticky-navbar' : ''}`}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/overview" element={<Overview />} />
                  <Route path="/administrative-structure" element={<AdministrativeStructure />} />
                  <Route path="/board-members" element={<BoardMembers />} />
                  <Route path="/board-project" element={<BoardProject />} />
                  <Route path="/annual-report" element={<AnnualReport />} />
                  <Route path="/circulars" element={<Circulars />} />
                  <Route path="/workers-welfare" element={<WorkersWelfare />} />
                  <Route path="/100-days-program" element={<HundredDaysProgram />} />
                  <Route path="/right-to-info" element={<RightToInfo />} />
                  <Route path="/right-to-service" element={<RightToService />} />
                  <Route path="/tenders-notices" element={<TendersNotices />} />
                  <Route path="/photo-gallery" element={<PhotoGallery />} />
                  <Route path="/photo-gallery/:year" element={<PhotoGallery />} />
                  <Route path="/photo-gallery/:year/:albumId" element={<PhotoGallery />} />
                  <Route path="/video-gallery" element={<VideoGallery />} />
                  <Route path="/video-gallery/:year" element={<VideoGallery />} />
                  <Route path="/video-gallery/:year/:videoId" element={<VideoGallery />} />
                  <Route path="/screen-reader" element={<ScreenReader />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/list-of-services" element={<ListOfServices />} />
                  <Route path="/important-circulars" element={<ImportantCirculars />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/contact-sop" element={<ContactSOP />} />
         <Route path="/sitemap" element={<SiteMap />} />
        <Route path="/grievance" element={<Grievance />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/faq" element={<FAQ />} />
                  <Route path="/schemes" element={<Schemes />} />
                  <Route path="/initiatives" element={<Navigate to="/activities" replace />} />
                  <Route path="/activities" element={<Activities />} />
                  <Route path="/programs" element={<Programs />} />
                  <Route path="/awards" element={<Awards />} />
                  <Route path="/feedback" element={<Feedback />} />
                </Routes>
              </main>
              <Footer />
              <GoToTopButton />
            </div>
          </Router>
          </ExternalLinkProvider>
        </AccessibilityProvider>
      </ThemeProvider>
    </LanguageProvider>
  );
}

export default App;
